/** Namespace names — must match keys in src/i18n/index.ts resources */
export const NS = {
  common: 'common',
  organizationSettings: 'organizationSettings',
  navigation: 'navigation',
  navbarMenus: 'navbarMenus',
  profile: 'profile',
  adminPages: 'adminPages',
  jobPortal: 'jobPortal',
  auth: 'auth',
  home: 'home',
  assessments: 'assessments',
} as const;

export type Namespace = (typeof NS)[keyof typeof NS];

export const COMMON_KEYS = {
  actions: {
    cancel: 'actions.cancel',
  },
  navbar: {
    welcome: 'navbar.welcome',
    companyLogoAlt: 'navbar.companyLogoAlt',
  },
  locale: {
    english: 'locale.english',
    indonesian: 'locale.indonesian',
    sinhala: 'locale.sinhala',
    tamil: 'locale.tamil',
    russian: 'locale.russian',
    switchLabel: 'locale.switchLabel',
  },
} as const;

export const NAVIGATION_KEYS = {
  sidebar: {
    dashboard: 'sidebar.dashboard',
    hierTalent: 'sidebar.hierTalent',
    questions: 'sidebar.questions',
    questionBank: 'sidebar.questionBank',
    preScreenQuestions: 'sidebar.preScreenQuestions',
    contentManager: 'sidebar.contentManager',
    autoGenerateQuestions: 'sidebar.autoGenerateQuestions',
    ccaQuestions: 'sidebar.ccaQuestions',
    jobPostings: 'sidebar.jobPostings',
    candibankExplorer: 'sidebar.candibankExplorer',
    reporting: 'sidebar.reporting',
  },
  mobileMenu: {
    navigation: 'mobileMenu.navigation',
  },
} as const;

export const NAVBAR_MENU_KEYS = {
  systemSettings: {
    title: 'systemSettings.title',
    description: 'systemSettings.description',
    footerConfiguration: 'systemSettings.footerConfiguration',
    online: 'systemSettings.online',
    items: {
      usersRoles: {
        title: 'systemSettings.items.usersRoles.title',
        description: 'systemSettings.items.usersRoles.description',
      },
      assessmentFramework: {
        title: 'systemSettings.items.assessmentFramework.title',
        description: 'systemSettings.items.assessmentFramework.description',
      },
      organizationSettings: {
        title: 'systemSettings.items.organizationSettings.title',
        description: 'systemSettings.items.organizationSettings.description',
      },
      employeeManagement: {
        title: 'systemSettings.items.employeeManagement.title',
        description: 'systemSettings.items.employeeManagement.description',
      },
      candidateManagement: {
        title: 'systemSettings.items.candidateManagement.title',
        description: 'systemSettings.items.candidateManagement.description',
      },
      partnerManagement: {
        title: 'systemSettings.items.partnerManagement.title',
        description: 'systemSettings.items.partnerManagement.description',
      },
    },
  },
  notifications: {
    welcome: 'notifications.welcome',
    assessmentReady: 'notifications.assessmentReady',
    newApplication: 'notifications.newApplication',
    empty: 'notifications.empty',
    title: 'notifications.title',
    emptyMobile: 'notifications.emptyMobile',
  },
  profile: {
    userAvatarAlt: 'profile.userAvatarAlt',
    defaultUserName: 'profile.defaultUserName',
    quickActions: 'profile.quickActions',
    online: 'profile.online',
    settings: 'profile.settings',
    items: {
      profileSettings: {
        title: 'profile.items.profileSettings.title',
        description: 'profile.items.profileSettings.description',
      },
      changePassword: {
        title: 'profile.items.changePassword.title',
        description: 'profile.items.changePassword.description',
      },
    },
    signOut: {
      title: 'profile.signOut.title',
      description: 'profile.signOut.description',
    },
    logout: 'profile.logout',
    deleteAccountConfirm: 'profile.deleteAccountConfirm',
  },
  views: {
    admin: 'views.admin',
    participant: 'views.participant',
    hr: 'views.hr',
  },
  roles: {
    employee: 'roles.employee',
    candidate: 'roles.candidate',
    admin: 'roles.admin',
    user: 'roles.user',
  },
} as const;

export const PROFILE_KEYS = {
  page: {
    title: 'page.title',
    subtitle: 'page.subtitle',
    profileAlt: 'page.profileAlt',
  },
  sections: {
    personal: {
      title: 'sections.personal.title',
      description: 'sections.personal.description',
    },
    account: {
      title: 'sections.account.title',
      description: 'sections.account.description',
      intro: 'sections.account.intro',
    },
    notifications: {
      title: 'sections.notifications.title',
      description: 'sections.notifications.description',
      heading: 'sections.notifications.heading',
      intro: 'sections.notifications.intro',
      items: {
        emailUpdates: {
          label: 'sections.notifications.items.emailUpdates.label',
          description: 'sections.notifications.items.emailUpdates.description',
        },
        assessmentReminders: {
          label: 'sections.notifications.items.assessmentReminders.label',
          description:
            'sections.notifications.items.assessmentReminders.description',
        },
        systemNotifications: {
          label: 'sections.notifications.items.systemNotifications.label',
          description:
            'sections.notifications.items.systemNotifications.description',
        },
      },
    },
    privacy: {
      title: 'sections.privacy.title',
      description: 'sections.privacy.description',
      intro: 'sections.privacy.intro',
      policyTitle: 'sections.privacy.policyTitle',
      policyDescription: 'sections.privacy.policyDescription',
      readPolicy: 'sections.privacy.readPolicy',
      dataManagement: 'sections.privacy.dataManagement',
      downloadData: 'sections.privacy.downloadData',
      downloadDataDesc: 'sections.privacy.downloadDataDesc',
      requestDownload: 'sections.privacy.requestDownload',
      deleteAccount: 'sections.privacy.deleteAccount',
      deleteAccountDesc: 'sections.privacy.deleteAccountDesc',
    },
    candidate: {
      title: 'sections.candidate.title',
      description: 'sections.candidate.description',
      intro: 'sections.candidate.intro',
      jobSeekingStatus: 'sections.candidate.jobSeekingStatus',
      resume: 'sections.candidate.resume',
      resumeHint: 'sections.candidate.resumeHint',
      uploadClick: 'sections.candidate.uploadClick',
      uploadOrDrag: 'sections.candidate.uploadOrDrag',
      uploadFormats: 'sections.candidate.uploadFormats',
      resumeFileName: 'sections.candidate.resumeFileName',
      resumeMeta: 'sections.candidate.resumeMeta',
      viewResume: 'sections.candidate.viewResume',
      downloadResume: 'sections.candidate.downloadResume',
      deleteResume: 'sections.candidate.deleteResume',
      careerStatus: {
        activelyLooking: 'sections.candidate.careerStatus.activelyLooking',
        openToOpportunities:
          'sections.candidate.careerStatus.openToOpportunities',
        notAvailable: 'sections.candidate.careerStatus.notAvailable',
      },
    },
  },
  actions: {
    editProfile: 'actions.editProfile',
    save: 'actions.save',
    cancel: 'actions.cancel',
    changePassword: 'actions.changePassword',
    comingSoon: 'actions.comingSoon',
  },
  fields: {
    firstName: 'fields.firstName',
    lastName: 'fields.lastName',
    email: 'fields.email',
    phone: 'fields.phone',
    jobTitle: 'fields.jobTitle',
    department: 'fields.department',
    bio: 'fields.bio',
    bioPlaceholder: 'fields.bioPlaceholder',
    jobTitleFallback: 'fields.jobTitleFallback',
    departmentFallback: 'fields.departmentFallback',
  },
  account: {
    passwordSecurity: 'account.passwordSecurity',
    password: 'account.password',
    passwordLastUpdated: 'account.passwordLastUpdated',
    twoFactor: 'account.twoFactor',
    twoFactorDesc: 'account.twoFactorDesc',
    preferences: 'account.preferences',
    language: 'account.language',
    languageDesc: 'account.languageDesc',
    timeZone: 'account.timeZone',
    timeZoneDesc: 'account.timeZoneDesc',
  },
  messages: {
    updateSuccess: 'messages.updateSuccess',
    updateFailed: 'messages.updateFailed',
    pictureUnderReview: 'messages.pictureUnderReview',
    dataDownloadRequested: 'messages.dataDownloadRequested',
    resumePreview: 'messages.resumePreview',
    resumeDownloading: 'messages.resumeDownloading',
    resumeDeleted: 'messages.resumeDeleted',
  },
  picture: {
    pending: 'picture.pending',
    rejected: 'picture.rejected',
  },
  modal: {
    updatePhoto: 'modal.updatePhoto',
  },
} as const;

export const ADMIN_PAGES_KEYS = {
  breadcrumbs: {
    admin: 'breadcrumbs.admin',
    settings: 'breadcrumbs.settings',
  },
  unauthorized: {
    title: 'unauthorized.title',
    message: 'unauthorized.message',
    goHome: 'unauthorized.goHome',
    loginAgain: 'unauthorized.loginAgain',
  },
  accessManagement: {
    title: 'accessManagement.title',
    users: 'accessManagement.users',
    groups: 'accessManagement.groups',
    roles: 'accessManagement.roles',
    tabs: {
      groups: 'accessManagement.tabs.groups',
      roles: 'accessManagement.tabs.roles',
    },
    search: 'accessManagement.search',
    refresh: 'accessManagement.refresh',
    groupNameColumn: 'accessManagement.groupNameColumn',
  },
  assessmentFramework: {
    title: 'assessmentFramework.title',
    skills: 'assessmentFramework.skills',
    subSkills: 'assessmentFramework.subSkills',
    jobTitles: 'assessmentFramework.jobTitles',
    tabs: {
      skills: 'assessmentFramework.tabs.skills',
      jobTitles: 'assessmentFramework.tabs.jobTitles',
    },
    skillsCount: 'assessmentFramework.skillsCount',
    sortLabel: 'assessmentFramework.sortLabel',
    searchPlaceholder: 'assessmentFramework.searchPlaceholder',
    addSkillPlaceholder: 'assessmentFramework.addSkillPlaceholder',
    add: 'assessmentFramework.add',
  },
  employeeManagement: {
    title: 'employeeManagement.title',
    searchPlaceholder: 'employeeManagement.searchPlaceholder',
    filter: 'employeeManagement.filter',
    refresh: 'employeeManagement.refresh',
    inviteBulk: 'employeeManagement.inviteBulk',
    inviteUsers: 'employeeManagement.inviteUsers',
    columns: {
      user: 'employeeManagement.columns.user',
      groups: 'employeeManagement.columns.groups',
      lastActive: 'employeeManagement.columns.lastActive',
    },
    contextMenu: {
      update: 'employeeManagement.contextMenu.update',
      deleteItem: 'employeeManagement.contextMenu.deleteItem',
    },
    editModal: {
      jobTitle: 'employeeManagement.editModal.jobTitle',
      reportingManager: 'employeeManagement.editModal.reportingManager',
      organizationUnit: 'employeeManagement.editModal.organizationUnit',
      groupMembership: 'employeeManagement.editModal.groupMembership',
      roleMembership: 'employeeManagement.editModal.roleMembership',
      editEmployee: 'employeeManagement.editModal.editEmployee',
    },
    bulkUpload: {
      fileName: 'employeeManagement.bulkUpload.fileName',
      bulkInviteFromCsv: 'employeeManagement.bulkUpload.bulkInviteFromCsv',
      uploadFile: 'employeeManagement.bulkUpload.uploadFile',
      downloadSampleCsv: 'employeeManagement.bulkUpload.downloadSampleCsv',
      invalidFileType: 'employeeManagement.bulkUpload.invalidFileType',
    },
    row: {
      empty: 'employeeManagement.row.empty',
      neverLoggedIn: 'employeeManagement.row.neverLoggedIn',
      deleteConfirm: 'employeeManagement.row.deleteConfirm',
    },
    inviteModal: {
      title: 'employeeManagement.inviteModal.title',
      email: 'employeeManagement.inviteModal.email',
      firstName: 'employeeManagement.inviteModal.firstName',
      lastName: 'employeeManagement.inviteModal.lastName',
      jobTitle: 'employeeManagement.inviteModal.jobTitle',
      reportingManager: 'employeeManagement.inviteModal.reportingManager',
      organizationUnit: 'employeeManagement.inviteModal.organizationUnit',
      select: 'employeeManagement.inviteModal.select',
      search: 'employeeManagement.inviteModal.search',
      submit: 'employeeManagement.inviteModal.submit',
    },
  },
  organizationHierarchy: {
    organization: 'organizationHierarchy.organization',
    apply: 'organizationHierarchy.apply',
    createUnit: 'organizationHierarchy.createUnit',
    editName: 'organizationHierarchy.editName',
    deleteUnit: 'organizationHierarchy.deleteUnit',
  },
  jobOpenings: {
    createDialog: {
      title: 'jobOpenings.createDialog.title',
      subtitle: 'jobOpenings.createDialog.subtitle',
      selectAll: 'jobOpenings.createDialog.selectAll',
      clearAll: 'jobOpenings.createDialog.clearAll',
      selected: 'jobOpenings.createDialog.selected',
      cancel: 'jobOpenings.createDialog.cancel',
      creating: 'jobOpenings.createDialog.creating',
      createButton: 'jobOpenings.createDialog.createButton',
      helpText: {
        jobPosting: 'jobOpenings.createDialog.helpText.jobPosting',
        applicantAnalysis: 'jobOpenings.createDialog.helpText.applicantAnalysis',
        autoSuitability: 'jobOpenings.createDialog.helpText.autoSuitability',
        autoOutstanding: 'jobOpenings.createDialog.helpText.autoOutstanding',
        autoAssessments: 'jobOpenings.createDialog.helpText.autoAssessments',
        smartInterviews: 'jobOpenings.createDialog.helpText.smartInterviews',
        psychometric: 'jobOpenings.createDialog.helpText.psychometric',
        wellness: 'jobOpenings.createDialog.helpText.wellness',
        agenticAssessment: 'jobOpenings.createDialog.helpText.agenticAssessment',
      },
    },
    cancelDialog: {
      title: 'jobOpenings.cancelDialog.title',
      message: 'jobOpenings.cancelDialog.message',
      confirm: 'jobOpenings.cancelDialog.confirm',
      stay: 'jobOpenings.cancelDialog.stay',
    },
    jobRequisition: {
      notEditableBanner: 'jobOpenings.jobRequisition.notEditableBanner',
      notEditableDetail: 'jobOpenings.jobRequisition.notEditableDetail',
      typeLabel: 'jobOpenings.jobRequisition.typeLabel',
      typeHelperText: 'jobOpenings.jobRequisition.typeHelperText',
      deadlineLabel: 'jobOpenings.jobRequisition.deadlineLabel',
      deadlineHelperText: 'jobOpenings.jobRequisition.deadlineHelperText',
      scopeLabel: 'jobOpenings.jobRequisition.scopeLabel',
      scopeHelperText: 'jobOpenings.jobRequisition.scopeHelperText',
      hiringManagerLabel: 'jobOpenings.jobRequisition.hiringManagerLabel',
      hiringManagerHelperText: 'jobOpenings.jobRequisition.hiringManagerHelperText',
      hrRecruiterLabel: 'jobOpenings.jobRequisition.hrRecruiterLabel',
      hrRecruiterHelperText: 'jobOpenings.jobRequisition.hrRecruiterHelperText',
      tagsLabel: 'jobOpenings.jobRequisition.tagsLabel',
      tagsOptional: 'jobOpenings.jobRequisition.tagsOptional',
      tagsPlaceholder: 'jobOpenings.jobRequisition.tagsPlaceholder',
      saveAndContinue: 'jobOpenings.jobRequisition.saveAndContinue',
      typeReplacement: 'jobOpenings.jobRequisition.typeReplacement',
      typeNewRole: 'jobOpenings.jobRequisition.typeNewRole',
      scopeExternal: 'jobOpenings.jobRequisition.scopeExternal',
      scopeInternal: 'jobOpenings.jobRequisition.scopeInternal',
    },
    jobDescription: {
      notEditableBanner: 'jobOpenings.jobDescription.notEditableBanner',
      notEditableDetail: 'jobOpenings.jobDescription.notEditableDetail',
      showAll: 'jobOpenings.jobDescription.showAll',
      collapseAll: 'jobOpenings.jobDescription.collapseAll',
      configureDocuments: 'jobOpenings.jobDescription.configureDocuments',
      cvScoreWeighting: 'jobOpenings.jobDescription.cvScoreWeighting',
      cvScoreWeightingsTitle: 'jobOpenings.jobDescription.cvScoreWeightingsTitle',
      weightingLocation: 'jobOpenings.jobDescription.weightingLocation',
      weightingMinWorkExp: 'jobOpenings.jobDescription.weightingMinWorkExp',
      weightingMandatorySkills: 'jobOpenings.jobDescription.weightingMandatorySkills',
      weightingDesirableSkills: 'jobOpenings.jobDescription.weightingDesirableSkills',
      weightingBehavioural: 'jobOpenings.jobDescription.weightingBehavioural',
      generateJobDescription: 'jobOpenings.jobDescription.generateJobDescription',
      importJobDescription: 'jobOpenings.jobDescription.importJobDescription',
      loadJobTemplate: 'jobOpenings.jobDescription.loadJobTemplate',
      createJobTemplate: 'jobOpenings.jobDescription.createJobTemplate',
      detailsSectionTitle: 'jobOpenings.jobDescription.detailsSectionTitle',
      jobTitleLabel: 'jobOpenings.jobDescription.jobTitleLabel',
      jobReferenceNumberLabel: 'jobOpenings.jobDescription.jobReferenceNumberLabel',
      workTypeLabel: 'jobOpenings.jobDescription.workTypeLabel',
      minimumWorkExperienceLabel: 'jobOpenings.jobDescription.minimumWorkExperienceLabel',
      jobLocationLabel: 'jobOpenings.jobDescription.jobLocationLabel',
      fullyRemote: 'jobOpenings.jobDescription.fullyRemote',
      hybrid: 'jobOpenings.jobDescription.hybrid',
      fullyOnsite: 'jobOpenings.jobDescription.fullyOnsite',
      currentLocation: 'jobOpenings.jobDescription.currentLocation',
      locationPreview: 'jobOpenings.jobDescription.locationPreview',
      city: 'jobOpenings.jobDescription.city',
      stateProvince: 'jobOpenings.jobDescription.stateProvince',
      country: 'jobOpenings.jobDescription.country',
      saveJobDescription: 'jobOpenings.jobDescription.saveJobDescription',
      responsibilitiesTitle: 'jobOpenings.jobDescription.responsibilitiesTitle',
      educationsTitle: 'jobOpenings.jobDescription.educationsTitle',
      certificationsTitle: 'jobOpenings.jobDescription.certificationsTitle',
      mandatorySkillsTitle: 'jobOpenings.jobDescription.mandatorySkillsTitle',
      desirableSkillsTitle: 'jobOpenings.jobDescription.desirableSkillsTitle',
      behavioralAttributesTitle: 'jobOpenings.jobDescription.behavioralAttributesTitle',
    },
    ui: {
      clickToCollapse: 'jobOpenings.ui.clickToCollapse',
      clickToExpand: 'jobOpenings.ui.clickToExpand',
      noItemsAdded: 'jobOpenings.ui.noItemsAdded',
      noItemsAvailable: 'jobOpenings.ui.noItemsAvailable',
      addNew: 'jobOpenings.ui.addNew',
      typeSomething: 'jobOpenings.ui.typeSomething',
      stateLabel: 'jobOpenings.ui.stateLabel',
      cancel: 'jobOpenings.ui.cancel',
    },
    list: {
      breadcrumbParent: 'jobOpenings.list.breadcrumbParent',
      breadcrumbCurrent: 'jobOpenings.list.breadcrumbCurrent',
      viewJobPosting: 'jobOpenings.list.viewJobPosting',
      searchPlaceholder: 'jobOpenings.list.searchPlaceholder',
      filter: 'jobOpenings.list.filter',
      createNew: 'jobOpenings.list.createNew',
      noActiveLicenses: 'jobOpenings.list.noActiveLicenses',
      drawer: {
        title: 'jobOpenings.list.drawer.title',
        cancel: 'jobOpenings.list.drawer.cancel',
        reset: 'jobOpenings.list.drawer.reset',
        applyFilters: 'jobOpenings.list.drawer.applyFilters',
        timeRange: 'jobOpenings.list.drawer.timeRange',
        from: 'jobOpenings.list.drawer.from',
        to: 'jobOpenings.list.drawer.to',
        jobTitle: 'jobOpenings.list.drawer.jobTitle',
        jobReference: 'jobOpenings.list.drawer.jobReference',
        hiringManager: 'jobOpenings.list.drawer.hiringManager',
        internalRecruiter: 'jobOpenings.list.drawer.internalRecruiter',
        status: 'jobOpenings.list.drawer.status',
        statusPlaceholder: 'jobOpenings.list.drawer.statusPlaceholder',
        tags: 'jobOpenings.list.drawer.tags',
        tagsPlaceholder: 'jobOpenings.list.drawer.tagsPlaceholder',
      },
      statusOptions: {
        jobDescription: 'jobOpenings.list.statusOptions.jobDescription',
        jobPost: 'jobOpenings.list.statusOptions.jobPost',
        shortlist: 'jobOpenings.list.statusOptions.shortlist',
        interview: 'jobOpenings.list.statusOptions.interview',
        decisionMaking: 'jobOpenings.list.statusOptions.decisionMaking',
        closed: 'jobOpenings.list.statusOptions.closed',
      },
      table: {
        jobTitle: 'jobOpenings.list.table.jobTitle',
        jobReference: 'jobOpenings.list.table.jobReference',
        hiringManager: 'jobOpenings.list.table.hiringManager',
        internalRecruiter: 'jobOpenings.list.table.internalRecruiter',
        applicants: 'jobOpenings.list.table.applicants',
        creationDate: 'jobOpenings.list.table.creationDate',
        expiryDate: 'jobOpenings.list.table.expiryDate',
        tags: 'jobOpenings.list.table.tags',
        noTags: 'jobOpenings.list.table.noTags',
      },
    },
    pages: {
      jobOpeningDetails: 'jobOpenings.pages.jobOpeningDetails',
      applicantAnalysis: 'jobOpenings.pages.applicantAnalysis',
      autoAssessments: 'jobOpenings.pages.autoAssessments',
      smartInterviews: 'jobOpenings.pages.smartInterviews',
    },
    nav: {
      back: 'jobOpenings.nav.back',
      viewPosting: 'jobOpenings.nav.viewPosting',
      newJobOpening: 'jobOpenings.nav.newJobOpening',
      ref: 'jobOpenings.nav.ref',
    },
    autoAssessTab: {
      preScreenQuestions: 'jobOpenings.autoAssessTab.preScreenQuestions',
      skillQuestions: 'jobOpenings.autoAssessTab.skillQuestions',
      beiQuestions: 'jobOpenings.autoAssessTab.beiQuestions',
      addQuestions: 'jobOpenings.autoAssessTab.addQuestions',
      importFromBank: 'jobOpenings.autoAssessTab.importFromBank',
      searchFromBank: 'jobOpenings.autoAssessTab.searchFromBank',
      manualQuestion: 'jobOpenings.autoAssessTab.manualQuestion',
      typeQuestion: 'jobOpenings.autoAssessTab.typeQuestion',
      questionType: 'jobOpenings.autoAssessTab.questionType',
      addToAssessment: 'jobOpenings.autoAssessTab.addToAssessment',
      activeQuestions: 'jobOpenings.autoAssessTab.activeQuestions',
      total: 'jobOpenings.autoAssessTab.total',
      options: 'jobOpenings.autoAssessTab.options',
      addOption: 'jobOpenings.autoAssessTab.addOption',
      type: 'jobOpenings.autoAssessTab.type',
      cancel: 'jobOpenings.autoAssessTab.cancel',
      saveChanges: 'jobOpenings.autoAssessTab.saveChanges',
    },
    selectQuestions: {
      drawerTitle: 'jobOpenings.selectQuestions.drawerTitle',
      cancel: 'jobOpenings.selectQuestions.cancel',
      save: 'jobOpenings.selectQuestions.save',
      saving: 'jobOpenings.selectQuestions.saving',
      allQuestions: 'jobOpenings.selectQuestions.allQuestions',
      selectAll: 'jobOpenings.selectQuestions.selectAll',
      pageOf: 'jobOpenings.selectQuestions.pageOf',
      total: 'jobOpenings.selectQuestions.total',
      previous: 'jobOpenings.selectQuestions.previous',
      next: 'jobOpenings.selectQuestions.next',
      loading: 'jobOpenings.selectQuestions.loading',
      noQuestions: 'jobOpenings.selectQuestions.noQuestions',
      untitled: 'jobOpenings.selectQuestions.untitled',
      selectedQuestions: 'jobOpenings.selectQuestions.selectedQuestions',
      removeAll: 'jobOpenings.selectQuestions.removeAll',
      noSelected: 'jobOpenings.selectQuestions.noSelected',
    },
    questionFilter: {
      applyFilters: 'jobOpenings.questionFilter.applyFilters',
      selectSkill: 'jobOpenings.questionFilter.selectSkill',
      selectSubSkill1: 'jobOpenings.questionFilter.selectSubSkill1',
      selectSubSkill2: 'jobOpenings.questionFilter.selectSubSkill2',
      selectSubSkill3: 'jobOpenings.questionFilter.selectSubSkill3',
      selectSkillLevel: 'jobOpenings.questionFilter.selectSkillLevel',
      selectJobTitle: 'jobOpenings.questionFilter.selectJobTitle',
      searchQuestions: 'jobOpenings.questionFilter.searchQuestions',
    },
    skillQuestionsTab: {
      heading: 'jobOpenings.skillQuestionsTab.heading',
      score: 'jobOpenings.skillQuestionsTab.score',
      duration: 'jobOpenings.skillQuestionsTab.duration',
      questions: 'jobOpenings.skillQuestionsTab.questions',
      actions: 'jobOpenings.skillQuestionsTab.actions',
      loadTemplate: 'jobOpenings.skillQuestionsTab.loadTemplate',
      createTemplate: 'jobOpenings.skillQuestionsTab.createTemplate',
      removeAllQuestions: 'jobOpenings.skillQuestionsTab.removeAllQuestions',
      collapseAll: 'jobOpenings.skillQuestionsTab.collapseAll',
      expandAll: 'jobOpenings.skillQuestionsTab.expandAll',
      selectQuestions: 'jobOpenings.skillQuestionsTab.selectQuestions',
      quickBuild: 'jobOpenings.skillQuestionsTab.quickBuild',
      createNew: 'jobOpenings.skillQuestionsTab.createNew',
      deleteAllConfirm: 'jobOpenings.skillQuestionsTab.deleteAllConfirm',
      deleteAll: 'jobOpenings.skillQuestionsTab.deleteAll',
      cancel: 'jobOpenings.skillQuestionsTab.cancel',
      saveTemplate: 'jobOpenings.skillQuestionsTab.saveTemplate',
    },
    autoEvaluationTab: {
      notEditable: 'jobOpenings.autoEvaluationTab.notEditable',
      notEditableReason: 'jobOpenings.autoEvaluationTab.notEditableReason',
      showAll: 'jobOpenings.autoEvaluationTab.showAll',
      collapseAll: 'jobOpenings.autoEvaluationTab.collapseAll',
      importFromSuitability: 'jobOpenings.autoEvaluationTab.importFromSuitability',
      jobDescriptionDetails: 'jobOpenings.autoEvaluationTab.jobDescriptionDetails',
      jobLocation: 'jobOpenings.autoEvaluationTab.jobLocation',
      age: 'jobOpenings.autoEvaluationTab.age',
      workExperience: 'jobOpenings.autoEvaluationTab.workExperience',
      greaterThan: 'jobOpenings.autoEvaluationTab.greaterThan',
      lesserThan: 'jobOpenings.autoEvaluationTab.lesserThan',
    },
    listFields: {
      educations: 'jobOpenings.listFields.educations',
      certifications: 'jobOpenings.listFields.certifications',
      skills: 'jobOpenings.listFields.skills',
      school: 'jobOpenings.listFields.school',
      keywords: 'jobOpenings.listFields.keywords',
      behavioralAttributes: 'jobOpenings.listFields.behavioralAttributes',
      noItems: 'jobOpenings.listFields.noItems',
      addNewHint: 'jobOpenings.listFields.addNewHint',
      addNew: 'jobOpenings.listFields.addNew',
      cancelInput: 'jobOpenings.listFields.cancelInput',
      newItemPlaceholder: 'jobOpenings.listFields.newItemPlaceholder',
      addDesirable: 'jobOpenings.listFields.addDesirable',
      removeDesirable: 'jobOpenings.listFields.removeDesirable',
    },
    quickBuild: {
      drawerTitle: 'jobOpenings.quickBuild.drawerTitle',
      skillsConfigured: 'jobOpenings.quickBuild.skillsConfigured',
      totalPoints: 'jobOpenings.quickBuild.totalPoints',
      close: 'jobOpenings.quickBuild.close',
      skillConfiguration: 'jobOpenings.quickBuild.skillConfiguration',
      removeSelected: 'jobOpenings.quickBuild.removeSelected',
      unsavedChanges: 'jobOpenings.quickBuild.unsavedChanges',
      colSkill: 'jobOpenings.quickBuild.colSkill',
      colLevel: 'jobOpenings.quickBuild.colLevel',
      colQs: 'jobOpenings.quickBuild.colQs',
      colPoints: 'jobOpenings.quickBuild.colPoints',
      colTime: 'jobOpenings.quickBuild.colTime',
      colType: 'jobOpenings.quickBuild.colType',
      noSkillsTitle: 'jobOpenings.quickBuild.noSkillsTitle',
      noSkillsHint: 'jobOpenings.quickBuild.noSkillsHint',
      addFirstSkill: 'jobOpenings.quickBuild.addFirstSkill',
      addAnotherSkill: 'jobOpenings.quickBuild.addAnotherSkill',
      saveSkills: 'jobOpenings.quickBuild.saveSkills',
      saving: 'jobOpenings.quickBuild.saving',
      configuration: 'jobOpenings.quickBuild.configuration',
      jobTitle: 'jobOpenings.quickBuild.jobTitle',
      jobTitleHint: 'jobOpenings.quickBuild.jobTitleHint',
      autoGenerate: 'jobOpenings.quickBuild.autoGenerate',
      generating: 'jobOpenings.quickBuild.generating',
      suggestedSkills: 'jobOpenings.quickBuild.suggestedSkills',
      selectAll: 'jobOpenings.quickBuild.selectAll',
      noSuggestions: 'jobOpenings.quickBuild.noSuggestions',
      refreshSuggestions: 'jobOpenings.quickBuild.refreshSuggestions',
    },
    addQuestionModal: {
      question: 'jobOpenings.addQuestionModal.question',
      answer: 'jobOpenings.addQuestionModal.answer',
      skill: 'jobOpenings.addQuestionModal.skill',
      subSkill1: 'jobOpenings.addQuestionModal.subSkill1',
      subSkill2: 'jobOpenings.addQuestionModal.subSkill2',
      subSkill3: 'jobOpenings.addQuestionModal.subSkill3',
      level: 'jobOpenings.addQuestionModal.level',
      jobTitle: 'jobOpenings.addQuestionModal.jobTitle',
      selectJobTitles: 'jobOpenings.addQuestionModal.selectJobTitles',
      questionType: 'jobOpenings.addQuestionModal.questionType',
      timerSeconds: 'jobOpenings.addQuestionModal.timerSeconds',
      pointsForQuestion: 'jobOpenings.addQuestionModal.pointsForQuestion',
      addQuestion: 'jobOpenings.addQuestionModal.addQuestion',
    },
    progressTracker: {
      labels: {
        jobDescription: 'jobOpenings.progressTracker.labels.jobDescription',
        jobPost: 'jobOpenings.progressTracker.labels.jobPost',
        shortlisting: 'jobOpenings.progressTracker.labels.shortlisting',
        interviews: 'jobOpenings.progressTracker.labels.interviews',
        decisionMaking: 'jobOpenings.progressTracker.labels.decisionMaking',
        closed: 'jobOpenings.progressTracker.labels.closed',
      },
      statuses: {
        inactive: 'jobOpenings.progressTracker.statuses.inactive',
        pending: 'jobOpenings.progressTracker.statuses.pending',
        inProgress: 'jobOpenings.progressTracker.statuses.inProgress',
        completed: 'jobOpenings.progressTracker.statuses.completed',
        decisionInProgress: 'jobOpenings.progressTracker.statuses.decisionInProgress',
        offerInProgress: 'jobOpenings.progressTracker.statuses.offerInProgress',
        offerAccepted: 'jobOpenings.progressTracker.statuses.offerAccepted',
        noSuitableCandidate: 'jobOpenings.progressTracker.statuses.noSuitableCandidate',
        noOffersAccepted: 'jobOpenings.progressTracker.statuses.noOffersAccepted',
        jobRequestWithdrawn: 'jobOpenings.progressTracker.statuses.jobRequestWithdrawn',
      },
    },
    sidebar: {
      jobRequisition: 'jobOpenings.sidebar.jobRequisition',
      jobDescription: 'jobOpenings.sidebar.jobDescription',
      autoSuitability: 'jobOpenings.sidebar.autoSuitability',
      autoOutstanding: 'jobOpenings.sidebar.autoOutstanding',
      autoAssessQuestions: 'jobOpenings.sidebar.autoAssessQuestions',
      jobPosting: 'jobOpenings.sidebar.jobPosting',
      cvIngestion: 'jobOpenings.sidebar.cvIngestion',
      ccaQuestions: 'jobOpenings.sidebar.ccaQuestions',
      permissions: 'jobOpenings.sidebar.permissions',
    },
    smartInterviews: {
      tabs: {
        overview: 'jobOpenings.smartInterviews.tabs.overview',
        configuration: 'jobOpenings.smartInterviews.tabs.configuration',
        invitationTemplate: 'jobOpenings.smartInterviews.tabs.invitationTemplate',
        candidates: 'jobOpenings.smartInterviews.tabs.candidates',
        results: 'jobOpenings.smartInterviews.tabs.results',
        offer: 'jobOpenings.smartInterviews.tabs.offer',
      },
      newRound: 'jobOpenings.smartInterviews.newRound',
      noContent: 'jobOpenings.smartInterviews.noContent',
      loading: {
        rounds: 'jobOpenings.smartInterviews.loading.rounds',
        overview: 'jobOpenings.smartInterviews.loading.overview',
        configuration: 'jobOpenings.smartInterviews.loading.configuration',
        candidates: 'jobOpenings.smartInterviews.loading.candidates',
        results: 'jobOpenings.smartInterviews.loading.results',
        offer: 'jobOpenings.smartInterviews.loading.offer',
        invitationTemplate: 'jobOpenings.smartInterviews.loading.invitationTemplate',
      },
      overview: {
        interviewApplicantStatus: 'jobOpenings.smartInterviews.overview.interviewApplicantStatus',
        interviewTypes: 'jobOpenings.smartInterviews.overview.interviewTypes',
        interviewStatus: 'jobOpenings.smartInterviews.overview.interviewStatus',
        upcomingInterviews: 'jobOpenings.smartInterviews.overview.upcomingInterviews',
        noUpcoming: 'jobOpenings.smartInterviews.overview.noUpcoming',
        scheduledWillAppear: 'jobOpenings.smartInterviews.overview.scheduledWillAppear',
        loading: 'jobOpenings.smartInterviews.overview.loading',
      },
      configurations: {
        roundTitle: 'jobOpenings.smartInterviews.configurations.roundTitle',
        roundTitlePlaceholder: 'jobOpenings.smartInterviews.configurations.roundTitlePlaceholder',
        host: 'jobOpenings.smartInterviews.configurations.host',
        panelists: 'jobOpenings.smartInterviews.configurations.panelists',
        cancel: 'jobOpenings.smartInterviews.configurations.cancel',
        createRound: 'jobOpenings.smartInterviews.configurations.createRound',
        updateRound: 'jobOpenings.smartInterviews.configurations.updateRound',
        errors: {
          title: 'jobOpenings.smartInterviews.configurations.errors.title',
          host: 'jobOpenings.smartInterviews.configurations.errors.host',
          panelists: 'jobOpenings.smartInterviews.configurations.errors.panelists',
          dates: 'jobOpenings.smartInterviews.configurations.errors.dates',
          times: 'jobOpenings.smartInterviews.configurations.errors.times',
        },
      },
      candidates: {
        inviteButton: 'jobOpenings.smartInterviews.candidates.inviteButton',
        status: {
          invited: 'jobOpenings.smartInterviews.candidates.status.invited',
          scheduled: 'jobOpenings.smartInterviews.candidates.status.scheduled',
          confirmed: 'jobOpenings.smartInterviews.candidates.status.confirmed',
          live: 'jobOpenings.smartInterviews.candidates.status.live',
          finished: 'jobOpenings.smartInterviews.candidates.status.finished',
        },
        columns: {
          applicant: 'jobOpenings.smartInterviews.candidates.columns.applicant',
          interviewType: 'jobOpenings.smartInterviews.candidates.columns.interviewType',
          linkOrAddress: 'jobOpenings.smartInterviews.candidates.columns.linkOrAddress',
          dateTime: 'jobOpenings.smartInterviews.candidates.columns.dateTime',
          applicantStatus: 'jobOpenings.smartInterviews.candidates.columns.applicantStatus',
          notes: 'jobOpenings.smartInterviews.candidates.columns.notes',
        },
        deleteDialog: {
          title: 'jobOpenings.smartInterviews.candidates.deleteDialog.title',
          warning: 'jobOpenings.smartInterviews.candidates.deleteDialog.warning',
          cancel: 'jobOpenings.smartInterviews.candidates.deleteDialog.cancel',
          confirm: 'jobOpenings.smartInterviews.candidates.deleteDialog.confirm',
        },
        updateDialog: {
          title: 'jobOpenings.smartInterviews.candidates.updateDialog.title',
          virtual: 'jobOpenings.smartInterviews.candidates.updateDialog.virtual',
          inPerson: 'jobOpenings.smartInterviews.candidates.updateDialog.inPerson',
          meetingLink: 'jobOpenings.smartInterviews.candidates.updateDialog.meetingLink',
          physicalAddress: 'jobOpenings.smartInterviews.candidates.updateDialog.physicalAddress',
          cancel: 'jobOpenings.smartInterviews.candidates.updateDialog.cancel',
          save: 'jobOpenings.smartInterviews.candidates.updateDialog.save',
        },
        confirmDialog: {
          title: 'jobOpenings.smartInterviews.candidates.confirmDialog.title',
          when: 'jobOpenings.smartInterviews.candidates.confirmDialog.when',
          type: 'jobOpenings.smartInterviews.candidates.confirmDialog.type',
          meetingLink: 'jobOpenings.smartInterviews.candidates.confirmDialog.meetingLink',
          location: 'jobOpenings.smartInterviews.candidates.confirmDialog.location',
          confirmNote: 'jobOpenings.smartInterviews.candidates.confirmDialog.confirmNote',
          cancel: 'jobOpenings.smartInterviews.candidates.confirmDialog.cancel',
          confirm: 'jobOpenings.smartInterviews.candidates.confirmDialog.confirm',
        },
        invite: {
          drawerTitle: 'jobOpenings.smartInterviews.candidates.invite.drawerTitle',
          availableCandidates: 'jobOpenings.smartInterviews.candidates.invite.availableCandidates',
          subtitle: 'jobOpenings.smartInterviews.candidates.invite.subtitle',
          selectAll: 'jobOpenings.smartInterviews.candidates.invite.selectAll',
          removeAll: 'jobOpenings.smartInterviews.candidates.invite.removeAll',
          selectedCandidates: 'jobOpenings.smartInterviews.candidates.invite.selectedCandidates',
          noCandidates: 'jobOpenings.smartInterviews.candidates.invite.noCandidates',
          noSelected: 'jobOpenings.smartInterviews.candidates.invite.noSelected',
          filterName: 'jobOpenings.smartInterviews.candidates.invite.filterName',
          filterEmail: 'jobOpenings.smartInterviews.candidates.invite.filterEmail',
          filterStatus: 'jobOpenings.smartInterviews.candidates.invite.filterStatus',
          filterByStatus: 'jobOpenings.smartInterviews.candidates.invite.filterByStatus',
          searchByName: 'jobOpenings.smartInterviews.candidates.invite.searchByName',
          searchByEmail: 'jobOpenings.smartInterviews.candidates.invite.searchByEmail',
          clearFilters: 'jobOpenings.smartInterviews.candidates.invite.clearFilters',
          cancel: 'jobOpenings.smartInterviews.candidates.invite.cancel',
          sendInvitations: 'jobOpenings.smartInterviews.candidates.invite.sendInvitations',
        },
      },
      results: {
        generateReport: 'jobOpenings.smartInterviews.results.generateReport',
        legend: {
          excellent: 'jobOpenings.smartInterviews.results.legend.excellent',
          good: 'jobOpenings.smartInterviews.results.legend.good',
          average: 'jobOpenings.smartInterviews.results.legend.average',
          notSuitable: 'jobOpenings.smartInterviews.results.legend.notSuitable',
        },
        columns: {
          applicant: 'jobOpenings.smartInterviews.results.columns.applicant',
          startTime: 'jobOpenings.smartInterviews.results.columns.startTime',
          type: 'jobOpenings.smartInterviews.results.columns.type',
          applicantStatus: 'jobOpenings.smartInterviews.results.columns.applicantStatus',
          status: 'jobOpenings.smartInterviews.results.columns.status',
          aiScore: 'jobOpenings.smartInterviews.results.columns.aiScore',
          skills: {
            technical: 'jobOpenings.smartInterviews.results.columns.skills.technical',
            experience: 'jobOpenings.smartInterviews.results.columns.skills.experience',
            industry: 'jobOpenings.smartInterviews.results.columns.skills.industry',
            product: 'jobOpenings.smartInterviews.results.columns.skills.product',
            communication: 'jobOpenings.smartInterviews.results.columns.skills.communication',
            problemSolving: 'jobOpenings.smartInterviews.results.columns.skills.problemSolving',
            leadership: 'jobOpenings.smartInterviews.results.columns.skills.leadership',
            collaboration: 'jobOpenings.smartInterviews.results.columns.skills.collaboration',
          },
        },
        userDetails: {
          evaluationMetrics: 'jobOpenings.smartInterviews.results.userDetails.evaluationMetrics',
          aiAnalysis: 'jobOpenings.smartInterviews.results.userDetails.aiAnalysis',
          assessmentJustification: 'jobOpenings.smartInterviews.results.userDetails.assessmentJustification',
          noJustification: 'jobOpenings.smartInterviews.results.userDetails.noJustification',
          tabs: {
            highlights: 'jobOpenings.smartInterviews.results.userDetails.tabs.highlights',
            transcripts: 'jobOpenings.smartInterviews.results.userDetails.tabs.transcripts',
            qa: 'jobOpenings.smartInterviews.results.userDetails.tabs.qa',
            panelistNotes: 'jobOpenings.smartInterviews.results.userDetails.tabs.panelistNotes',
          },
          scores: {
            overall: 'jobOpenings.smartInterviews.results.userDetails.scores.overall',
            technical: 'jobOpenings.smartInterviews.results.userDetails.scores.technical',
            roleExperience: 'jobOpenings.smartInterviews.results.userDetails.scores.roleExperience',
            industrySkills: 'jobOpenings.smartInterviews.results.userDetails.scores.industrySkills',
            productKnowledge: 'jobOpenings.smartInterviews.results.userDetails.scores.productKnowledge',
            communication: 'jobOpenings.smartInterviews.results.userDetails.scores.communication',
            problemSolving: 'jobOpenings.smartInterviews.results.userDetails.scores.problemSolving',
            leadership: 'jobOpenings.smartInterviews.results.userDetails.scores.leadership',
            teamCollaboration: 'jobOpenings.smartInterviews.results.userDetails.scores.teamCollaboration',
          },
          highlights: {
            noHighlights: 'jobOpenings.smartInterviews.results.userDetails.highlights.noHighlights',
            noHighlightsDesc: 'jobOpenings.smartInterviews.results.userDetails.highlights.noHighlightsDesc',
          },
        },
        report: {
          title: 'jobOpenings.smartInterviews.results.report.title',
          heading: 'jobOpenings.smartInterviews.results.report.heading',
          subtitle: 'jobOpenings.smartInterviews.results.report.subtitle',
          generate: 'jobOpenings.smartInterviews.results.report.generate',
          close: 'jobOpenings.smartInterviews.results.report.close',
          download: 'jobOpenings.smartInterviews.results.report.download',
          sections: {
            summary: 'jobOpenings.smartInterviews.results.report.sections.summary',
            questionAndAnswer: 'jobOpenings.smartInterviews.results.report.sections.questionAndAnswer',
            transcript: 'jobOpenings.smartInterviews.results.report.sections.transcript',
            technicalSkills: 'jobOpenings.smartInterviews.results.report.sections.technicalSkills',
            industrySkills: 'jobOpenings.smartInterviews.results.report.sections.industrySkills',
            productKnowledge: 'jobOpenings.smartInterviews.results.report.sections.productKnowledge',
            communication: 'jobOpenings.smartInterviews.results.report.sections.communication',
            problemSolving: 'jobOpenings.smartInterviews.results.report.sections.problemSolving',
            leadership: 'jobOpenings.smartInterviews.results.report.sections.leadership',
            teamCollaboration: 'jobOpenings.smartInterviews.results.report.sections.teamCollaboration',
            suggestedQuestions: 'jobOpenings.smartInterviews.results.report.sections.suggestedQuestions',
            interviewType: 'jobOpenings.smartInterviews.results.report.sections.interviewType',
          },
          toasts: {
            generated: 'jobOpenings.smartInterviews.results.report.toasts.generated',
            failed: 'jobOpenings.smartInterviews.results.report.toasts.failed',
            missingRoundId: 'jobOpenings.smartInterviews.results.report.toasts.missingRoundId',
            failedStatus: 'jobOpenings.smartInterviews.results.report.toasts.failedStatus',
            failedGenerate: 'jobOpenings.smartInterviews.results.report.toasts.failedGenerate',
          },
        },
      },
      offer: {
        selectCandidate: 'jobOpenings.smartInterviews.offer.selectCandidate',
        emptyState: {
          title: 'jobOpenings.smartInterviews.offer.emptyState.title',
          subtitle: 'jobOpenings.smartInterviews.offer.emptyState.subtitle',
          addButton: 'jobOpenings.smartInterviews.offer.emptyState.addButton',
          adding: 'jobOpenings.smartInterviews.offer.emptyState.adding',
          successToast: 'jobOpenings.smartInterviews.offer.emptyState.successToast',
          failedToast: 'jobOpenings.smartInterviews.offer.emptyState.failedToast',
          error: {
            title: 'jobOpenings.smartInterviews.offer.emptyState.error.title',
            suggestedActions: 'jobOpenings.smartInterviews.offer.emptyState.error.suggestedActions',
            checkResults: 'jobOpenings.smartInterviews.offer.emptyState.error.checkResults',
            verifyId: 'jobOpenings.smartInterviews.offer.emptyState.error.verifyId',
            tryRefreshing: 'jobOpenings.smartInterviews.offer.emptyState.error.tryRefreshing',
          },
        },
        userSelection: {
          total: 'jobOpenings.smartInterviews.offer.userSelection.total',
          selected: 'jobOpenings.smartInterviews.offer.userSelection.selected',
          avgScore: 'jobOpenings.smartInterviews.offer.userSelection.avgScore',
          previous: 'jobOpenings.smartInterviews.offer.userSelection.previous',
          next: 'jobOpenings.smartInterviews.offer.userSelection.next',
          page: 'jobOpenings.smartInterviews.offer.userSelection.page',
          of: 'jobOpenings.smartInterviews.offer.userSelection.of',
          status: {
            extended: 'jobOpenings.smartInterviews.offer.userSelection.status.extended',
            accepted: 'jobOpenings.smartInterviews.offer.userSelection.status.accepted',
            declined: 'jobOpenings.smartInterviews.offer.userSelection.status.declined',
            withheld: 'jobOpenings.smartInterviews.offer.userSelection.status.withheld',
          },
        },
        candidateDetails: {
          title: 'jobOpenings.smartInterviews.offer.candidateDetails.title',
          unknown: 'jobOpenings.smartInterviews.offer.candidateDetails.unknown',
          status: 'jobOpenings.smartInterviews.offer.candidateDetails.status',
          interviewType: 'jobOpenings.smartInterviews.offer.candidateDetails.interviewType',
          interviewDate: 'jobOpenings.smartInterviews.offer.candidateDetails.interviewDate',
          averageScore: 'jobOpenings.smartInterviews.offer.candidateDetails.averageScore',
          aiScore: 'jobOpenings.smartInterviews.offer.candidateDetails.aiScore',
          location: 'jobOpenings.smartInterviews.offer.candidateDetails.location',
          meetingLink: 'jobOpenings.smartInterviews.offer.candidateDetails.meetingLink',
          skillsAssessment: 'jobOpenings.smartInterviews.offer.candidateDetails.skillsAssessment',
          skills: {
            technicalSkills: 'jobOpenings.smartInterviews.offer.candidateDetails.skills.technicalSkills',
            roleExperience: 'jobOpenings.smartInterviews.offer.candidateDetails.skills.roleExperience',
            industrySkills: 'jobOpenings.smartInterviews.offer.candidateDetails.skills.industrySkills',
            productKnowledge: 'jobOpenings.smartInterviews.offer.candidateDetails.skills.productKnowledge',
            communication: 'jobOpenings.smartInterviews.offer.candidateDetails.skills.communication',
            problemSolving: 'jobOpenings.smartInterviews.offer.candidateDetails.skills.problemSolving',
            leadership: 'jobOpenings.smartInterviews.offer.candidateDetails.skills.leadership',
            teamCollaboration: 'jobOpenings.smartInterviews.offer.candidateDetails.skills.teamCollaboration',
          },
          makeFinalDecision: 'jobOpenings.smartInterviews.offer.candidateDetails.makeFinalDecision',
          finalDecisionSubtitle: 'jobOpenings.smartInterviews.offer.candidateDetails.finalDecisionSubtitle',
          confirmCheckbox: 'jobOpenings.smartInterviews.offer.candidateDetails.confirmCheckbox',
          rejectCandidate: 'jobOpenings.smartInterviews.offer.candidateDetails.rejectCandidate',
          sendOffer: 'jobOpenings.smartInterviews.offer.candidateDetails.sendOffer',
          pleaseConfirmFirst: 'jobOpenings.smartInterviews.offer.candidateDetails.pleaseConfirmFirst',
          offerExtended: 'jobOpenings.smartInterviews.offer.candidateDetails.offerExtended',
          offerSentTo: 'jobOpenings.smartInterviews.offer.candidateDetails.offerSentTo',
          candidateReviewing: 'jobOpenings.smartInterviews.offer.candidateDetails.candidateReviewing',
          withdrawOffer: 'jobOpenings.smartInterviews.offer.candidateDetails.withdrawOffer',
          candidateAcceptedOffer: 'jobOpenings.smartInterviews.offer.candidateDetails.candidateAcceptedOffer',
          offerAccepted: 'jobOpenings.smartInterviews.offer.candidateDetails.offerAccepted',
          hasAccepted: 'jobOpenings.smartInterviews.offer.candidateDetails.hasAccepted',
          congratulations: 'jobOpenings.smartInterviews.offer.candidateDetails.congratulations',
          offerRejectedDeclined: 'jobOpenings.smartInterviews.offer.candidateDetails.offerRejectedDeclined',
          wasRejected: 'jobOpenings.smartInterviews.offer.candidateDetails.wasRejected',
          wasDeclined: 'jobOpenings.smartInterviews.offer.candidateDetails.wasDeclined',
          notSelectedMessage: 'jobOpenings.smartInterviews.offer.candidateDetails.notSelectedMessage',
          declinedMessage: 'jobOpenings.smartInterviews.offer.candidateDetails.declinedMessage',
          extendRevisedOffer: 'jobOpenings.smartInterviews.offer.candidateDetails.extendRevisedOffer',
          offerWithheld: 'jobOpenings.smartInterviews.offer.candidateDetails.offerWithheld',
          offerOnHold: 'jobOpenings.smartInterviews.offer.candidateDetails.offerOnHold',
          withheldMessage: 'jobOpenings.smartInterviews.offer.candidateDetails.withheldMessage',
          extendOffer: 'jobOpenings.smartInterviews.offer.candidateDetails.extendOffer',
          acceptOffer: 'jobOpenings.smartInterviews.offer.candidateDetails.acceptOffer',
          rejectOffer: 'jobOpenings.smartInterviews.offer.candidateDetails.rejectOffer',
          withholdOffer: 'jobOpenings.smartInterviews.offer.candidateDetails.withholdOffer',
        },
      },
    },
    applicantAnalysis: {
      analysisTab: {
        filter: 'jobOpenings.applicantAnalysis.analysisTab.filter',
        threshold: 'jobOpenings.applicantAnalysis.analysisTab.threshold',
        views: 'jobOpenings.applicantAnalysis.analysisTab.views',
        actions: 'jobOpenings.applicantAnalysis.analysisTab.actions',
      },
      analyticAction: {
        selectAll: 'jobOpenings.applicantAnalysis.analyticAction.selectAll',
        openInTriage: 'jobOpenings.applicantAnalysis.analyticAction.openInTriage',
        moveToNew: 'jobOpenings.applicantAnalysis.analyticAction.moveToNew',
        moveToSuitable: 'jobOpenings.applicantAnalysis.analyticAction.moveToSuitable',
        moveToAutoAssess: 'jobOpenings.applicantAnalysis.analyticAction.moveToAutoAssess',
        moveToOutstanding: 'jobOpenings.applicantAnalysis.analyticAction.moveToOutstanding',
        moveToShortList: 'jobOpenings.applicantAnalysis.analyticAction.moveToShortList',
        moveToUnsuccessful: 'jobOpenings.applicantAnalysis.analyticAction.moveToUnsuccessful',
        deselectAll: 'jobOpenings.applicantAnalysis.analyticAction.deselectAll',
        noSelection: 'jobOpenings.applicantAnalysis.analyticAction.noSelection',
        noCandidates: 'jobOpenings.applicantAnalysis.analyticAction.noCandidates',
        instructionsLabel: 'jobOpenings.applicantAnalysis.analyticAction.instructionsLabel',
      },
      analyticFilter: {
        apply: 'jobOpenings.applicantAnalysis.analyticFilter.apply',
        close: 'jobOpenings.applicantAnalysis.analyticFilter.close',
        applyFilters: 'jobOpenings.applicantAnalysis.analyticFilter.applyFilters',
        graphAxisFilters: 'jobOpenings.applicantAnalysis.analyticFilter.graphAxisFilters',
        xSkills: 'jobOpenings.applicantAnalysis.analyticFilter.xSkills',
        yJobTitles: 'jobOpenings.applicantAnalysis.analyticFilter.yJobTitles',
        zBehavioral: 'jobOpenings.applicantAnalysis.analyticFilter.zBehavioral',
        keywordCounter: 'jobOpenings.applicantAnalysis.analyticFilter.keywordCounter',
        candidateAttributes: 'jobOpenings.applicantAnalysis.analyticFilter.candidateAttributes',
        applicantStatus: 'jobOpenings.applicantAnalysis.analyticFilter.applicantStatus',
        selectAppStatus: 'jobOpenings.applicantAnalysis.analyticFilter.selectAppStatus',
        gender: 'jobOpenings.applicantAnalysis.analyticFilter.gender',
        selectGender: 'jobOpenings.applicantAnalysis.analyticFilter.selectGender',
        male: 'jobOpenings.applicantAnalysis.analyticFilter.male',
        female: 'jobOpenings.applicantAnalysis.analyticFilter.female',
        other: 'jobOpenings.applicantAnalysis.analyticFilter.other',
        ageGroup: 'jobOpenings.applicantAnalysis.analyticFilter.ageGroup',
        includeUnknownAges: 'jobOpenings.applicantAnalysis.analyticFilter.includeUnknownAges',
        errorLoading: 'jobOpenings.applicantAnalysis.analyticFilter.errorLoading',
        saved: 'jobOpenings.applicantAnalysis.analyticFilter.saved',
        savedNoDb: 'jobOpenings.applicantAnalysis.analyticFilter.savedNoDb',
        skill: 'jobOpenings.applicantAnalysis.analyticFilter.skill',
        jobTitle: 'jobOpenings.applicantAnalysis.analyticFilter.jobTitle',
        attribute: 'jobOpenings.applicantAnalysis.analyticFilter.attribute',
        keyword: 'jobOpenings.applicantAnalysis.analyticFilter.keyword',
        statusNew: 'jobOpenings.applicantAnalysis.analyticFilter.statusNew',
        statusSuitable: 'jobOpenings.applicantAnalysis.analyticFilter.statusSuitable',
        statusAutoAssessment: 'jobOpenings.applicantAnalysis.analyticFilter.statusAutoAssessment',
        statusOutstanding: 'jobOpenings.applicantAnalysis.analyticFilter.statusOutstanding',
        statusShortlisted: 'jobOpenings.applicantAnalysis.analyticFilter.statusShortlisted',
        statusUnsuccessful: 'jobOpenings.applicantAnalysis.analyticFilter.statusUnsuccessful',
      },
      addNoteDialog: {
        title: 'jobOpenings.applicantAnalysis.addNoteDialog.title',
        close: 'jobOpenings.applicantAnalysis.addNoteDialog.close',
        conversationType: 'jobOpenings.applicantAnalysis.addNoteDialog.conversationType',
        email: 'jobOpenings.applicantAnalysis.addNoteDialog.email',
        phoneCall: 'jobOpenings.applicantAnalysis.addNoteDialog.phoneCall',
        inPerson: 'jobOpenings.applicantAnalysis.addNoteDialog.inPerson',
        videoCall: 'jobOpenings.applicantAnalysis.addNoteDialog.videoCall',
        other: 'jobOpenings.applicantAnalysis.addNoteDialog.other',
        conversationDateTime: 'jobOpenings.applicantAnalysis.addNoteDialog.conversationDateTime',
        subject: 'jobOpenings.applicantAnalysis.addNoteDialog.subject',
        notes: 'jobOpenings.applicantAnalysis.addNoteDialog.notes',
        subjectPlaceholder: 'jobOpenings.applicantAnalysis.addNoteDialog.subjectPlaceholder',
        notesPlaceholder: 'jobOpenings.applicantAnalysis.addNoteDialog.notesPlaceholder',
        beSpecific: 'jobOpenings.applicantAnalysis.addNoteDialog.beSpecific',
        characters: 'jobOpenings.applicantAnalysis.addNoteDialog.characters',
        cancel: 'jobOpenings.applicantAnalysis.addNoteDialog.cancel',
        save: 'jobOpenings.applicantAnalysis.addNoteDialog.save',
        fillRequired: 'jobOpenings.applicantAnalysis.addNoteDialog.fillRequired',
        addedSuccess: 'jobOpenings.applicantAnalysis.addNoteDialog.addedSuccess',
      },
      documentsTab: {
        noDocumentsTitle: 'jobOpenings.applicantAnalysis.documentsTab.noDocumentsTitle',
        noDocumentsDesc: 'jobOpenings.applicantAnalysis.documentsTab.noDocumentsDesc',
        uploaded: 'jobOpenings.applicantAnalysis.documentsTab.uploaded',
      },
      educationTab: {
        title: 'jobOpenings.applicantAnalysis.educationTab.title',
        graduated: 'jobOpenings.applicantAnalysis.educationTab.graduated',
        noEducation: 'jobOpenings.applicantAnalysis.educationTab.noEducation',
      },
      experienceTab: {
        title: 'jobOpenings.applicantAnalysis.experienceTab.title',
        total: 'jobOpenings.applicantAnalysis.experienceTab.total',
        lessThanMonth: 'jobOpenings.applicantAnalysis.experienceTab.lessThanMonth',
        monthsAbbr: 'jobOpenings.applicantAnalysis.experienceTab.monthsAbbr',
        monthsPluralAbbr: 'jobOpenings.applicantAnalysis.experienceTab.monthsPluralAbbr',
        yearsAbbr: 'jobOpenings.applicantAnalysis.experienceTab.yearsAbbr',
        yearsPluralAbbr: 'jobOpenings.applicantAnalysis.experienceTab.yearsPluralAbbr',
        present: 'jobOpenings.applicantAnalysis.experienceTab.present',
        noExperience: 'jobOpenings.applicantAnalysis.experienceTab.noExperience',
      },
      notesTab: {
        title: 'jobOpenings.applicantAnalysis.notesTab.title',
        noNotesTitle: 'jobOpenings.applicantAnalysis.notesTab.noNotesTitle',
        addNoteInstruction: 'jobOpenings.applicantAnalysis.notesTab.addNoteInstruction',
        unknownAuthor: 'jobOpenings.applicantAnalysis.notesTab.unknownAuthor',
        youLabel: 'jobOpenings.applicantAnalysis.notesTab.youLabel',
        save: 'jobOpenings.applicantAnalysis.notesTab.save',
        cancel: 'jobOpenings.applicantAnalysis.notesTab.cancel',
        deleteConfirmation: 'jobOpenings.applicantAnalysis.notesTab.deleteConfirmation',
        deleteButton: 'jobOpenings.applicantAnalysis.notesTab.deleteButton',
        addedOn: 'jobOpenings.applicantAnalysis.notesTab.addedOn',
      },
      overviewTab: {
        contactInfo: 'jobOpenings.applicantAnalysis.overviewTab.contactInfo',
        phone: 'jobOpenings.applicantAnalysis.overviewTab.phone',
        email: 'jobOpenings.applicantAnalysis.overviewTab.email',
        address: 'jobOpenings.applicantAnalysis.overviewTab.address',
        appStatus: 'jobOpenings.applicantAnalysis.overviewTab.appStatus',
        cvScore: 'jobOpenings.applicantAnalysis.overviewTab.cvScore',
        socialLinks: 'jobOpenings.applicantAnalysis.overviewTab.socialLinks',
        github: 'jobOpenings.applicantAnalysis.overviewTab.github',
        linkedin: 'jobOpenings.applicantAnalysis.overviewTab.linkedin',
        website: 'jobOpenings.applicantAnalysis.overviewTab.website',
      },
      preQualFilter: {
        title: 'jobOpenings.applicantAnalysis.preQualFilter.title',
        questionsAvailable: 'jobOpenings.applicantAnalysis.preQualFilter.questionsAvailable',
        selectAnswers: 'jobOpenings.applicantAnalysis.preQualFilter.selectAnswers',
        minValue: 'jobOpenings.applicantAnalysis.preQualFilter.minValue',
        maxValue: 'jobOpenings.applicantAnalysis.preQualFilter.maxValue',
        any: 'jobOpenings.applicantAnalysis.preQualFilter.any',
        country: 'jobOpenings.applicantAnalysis.preQualFilter.country',
        cityOptional: 'jobOpenings.applicantAnalysis.preQualFilter.cityOptional',
        countryExample: 'jobOpenings.applicantAnalysis.preQualFilter.countryExample',
        cityExample: 'jobOpenings.applicantAnalysis.preQualFilter.cityExample',
        radiusMode: 'jobOpenings.applicantAnalysis.preQualFilter.radiusMode',
        latitude: 'jobOpenings.applicantAnalysis.preQualFilter.latitude',
        longitude: 'jobOpenings.applicantAnalysis.preQualFilter.longitude',
        radiusKm: 'jobOpenings.applicantAnalysis.preQualFilter.radiusKm',
        textNotSupported: 'jobOpenings.applicantAnalysis.preQualFilter.textNotSupported',
        resetAll: 'jobOpenings.applicantAnalysis.preQualFilter.resetAll',
        cancel: 'jobOpenings.applicantAnalysis.preQualFilter.cancel',
        applyFilters: 'jobOpenings.applicantAnalysis.preQualFilter.applyFilters',
        saved: 'jobOpenings.applicantAnalysis.preQualFilter.saved',
        errorSaving: 'jobOpenings.applicantAnalysis.preQualFilter.errorSaving',
        saveFailed: 'jobOpenings.applicantAnalysis.preQualFilter.saveFailed',
      },
      searchResults: {
        title: 'jobOpenings.applicantAnalysis.searchResults.title',
        found: 'jobOpenings.applicantAnalysis.searchResults.found',
      },
      skillsTab: {
        allSkills: 'jobOpenings.applicantAnalysis.skillsTab.allSkills',
        noSkills: 'jobOpenings.applicantAnalysis.skillsTab.noSkills',
        matchedSkills: 'jobOpenings.applicantAnalysis.skillsTab.matchedSkills',
        noMatchedSkills: 'jobOpenings.applicantAnalysis.skillsTab.noMatchedSkills',
        behavioralAttributes: 'jobOpenings.applicantAnalysis.skillsTab.behavioralAttributes',
        noBehavioralAttributes: 'jobOpenings.applicantAnalysis.skillsTab.noBehavioralAttributes',
        matchedBehavioral: 'jobOpenings.applicantAnalysis.skillsTab.matchedBehavioral',
        noMatchedBehavioral: 'jobOpenings.applicantAnalysis.skillsTab.noMatchedBehavioral',
      },
      toolbar: {
        searchPlaceholder: 'jobOpenings.applicantAnalysis.toolbar.searchPlaceholder',
        hideResults: 'jobOpenings.applicantAnalysis.toolbar.hideResults',
        showResults: 'jobOpenings.applicantAnalysis.toolbar.showResults',
        clear: 'jobOpenings.applicantAnalysis.toolbar.clear',
        noResults: 'jobOpenings.applicantAnalysis.toolbar.noResults',
        selectAll: 'jobOpenings.applicantAnalysis.toolbar.selectAll',
        clearSelection: 'jobOpenings.applicantAnalysis.toolbar.clearSelection',
        emailCandidates: 'jobOpenings.applicantAnalysis.toolbar.emailCandidates',
      },
      emailConfirmation: {
        title: 'jobOpenings.applicantAnalysis.emailConfirmation.title',
        subtitle: 'jobOpenings.applicantAnalysis.emailConfirmation.subtitle',
        largeBatch: 'jobOpenings.applicantAnalysis.emailConfirmation.largeBatch',
        candidatesLabel: 'jobOpenings.applicantAnalysis.emailConfirmation.candidatesLabel',
        processingNote: 'jobOpenings.applicantAnalysis.emailConfirmation.processingNote',
        noEligible: 'jobOpenings.applicantAnalysis.emailConfirmation.noEligible',
        noEligibleDesc: 'jobOpenings.applicantAnalysis.emailConfirmation.noEligibleDesc',
        selectedCandidates: 'jobOpenings.applicantAnalysis.emailConfirmation.selectedCandidates',
        pending: 'jobOpenings.applicantAnalysis.emailConfirmation.pending',
        clearAll: 'jobOpenings.applicantAnalysis.emailConfirmation.clearAll',
        removeCandidate: 'jobOpenings.applicantAnalysis.emailConfirmation.removeCandidate',
        understand: 'jobOpenings.applicantAnalysis.emailConfirmation.understand',
        confirmPrefix: 'jobOpenings.applicantAnalysis.emailConfirmation.confirmPrefix',
        candidateLabel: 'jobOpenings.applicantAnalysis.emailConfirmation.candidateLabel',
        statusChange: 'jobOpenings.applicantAnalysis.emailConfirmation.statusChange',
        largeBatchNote: 'jobOpenings.applicantAnalysis.emailConfirmation.largeBatchNote',
        readyToSend: 'jobOpenings.applicantAnalysis.emailConfirmation.readyToSend',
        cancel: 'jobOpenings.applicantAnalysis.emailConfirmation.cancel',
        sending: 'jobOpenings.applicantAnalysis.emailConfirmation.sending',
        send: 'jobOpenings.applicantAnalysis.emailConfirmation.send',
      },
      columnConfig: {
        close: 'jobOpenings.applicantAnalysis.columnConfig.close',
        title: 'jobOpenings.applicantAnalysis.columnConfig.title',
        columnList: 'jobOpenings.applicantAnalysis.columnConfig.columnList',
        selectedColumns: 'jobOpenings.applicantAnalysis.columnConfig.selectedColumns',
      },
      columns: {
        applicant: 'jobOpenings.applicantAnalysis.columns.applicant',
        cvScore: 'jobOpenings.applicantAnalysis.columns.cvScore',
        status: 'jobOpenings.applicantAnalysis.columns.status',
        location: 'jobOpenings.applicantAnalysis.columns.location',
        experience: 'jobOpenings.applicantAnalysis.columns.experience',
        age: 'jobOpenings.applicantAnalysis.columns.age',
        gender: 'jobOpenings.applicantAnalysis.columns.gender',
        skills: 'jobOpenings.applicantAnalysis.columns.skills',
      },
      sidebarButtons: {
        all: 'jobOpenings.applicantAnalysis.sidebarButtons.all',
        newApplicants: 'jobOpenings.applicantAnalysis.sidebarButtons.newApplicants',
        suitable: 'jobOpenings.applicantAnalysis.sidebarButtons.suitable',
        outstanding: 'jobOpenings.applicantAnalysis.sidebarButtons.outstanding',
        assessment: 'jobOpenings.applicantAnalysis.sidebarButtons.assessment',
        unsuccessful: 'jobOpenings.applicantAnalysis.sidebarButtons.unsuccessful',
        shortlist: 'jobOpenings.applicantAnalysis.sidebarButtons.shortlist',
      },
      colorRange: {
        high: 'jobOpenings.applicantAnalysis.colorRange.high',
        medium: 'jobOpenings.applicantAnalysis.colorRange.medium',
        low: 'jobOpenings.applicantAnalysis.colorRange.low',
        pickHigh: 'jobOpenings.applicantAnalysis.colorRange.pickHigh',
        pickMedium: 'jobOpenings.applicantAnalysis.colorRange.pickMedium',
        pickLow: 'jobOpenings.applicantAnalysis.colorRange.pickLow',
        addMedium: 'jobOpenings.applicantAnalysis.colorRange.addMedium',
      },
      pdfDisplay: {
        loading: 'jobOpenings.applicantAnalysis.pdfDisplay.loading',
        noIframeSupport: 'jobOpenings.applicantAnalysis.pdfDisplay.noIframeSupport',
        noPdfSupport: 'jobOpenings.applicantAnalysis.pdfDisplay.noPdfSupport',
        downloadPdf: 'jobOpenings.applicantAnalysis.pdfDisplay.downloadPdf',
      },
      ccaResults: 'jobOpenings.applicantAnalysis.ccaResults',
    },
    jobPost: {
      publish: 'jobOpenings.jobPost.publish',
      disableEnable: 'jobOpenings.jobPost.disableEnable',
      tabs: {
        config: 'jobOpenings.jobPost.tabs.config',
        settings: 'jobOpenings.jobPost.tabs.settings',
        details: 'jobOpenings.jobPost.tabs.details',
        videoMessage: 'jobOpenings.jobPost.tabs.videoMessage',
        preQualifying: 'jobOpenings.jobPost.tabs.preQualifying',
        cca: 'jobOpenings.jobPost.tabs.cca',
        preview: 'jobOpenings.jobPost.tabs.preview',
      },
      settings: {
        timeRange: 'jobOpenings.jobPost.settings.timeRange',
        location: 'jobOpenings.jobPost.settings.location',
        department: 'jobOpenings.jobPost.settings.department',
        jobTitle: 'jobOpenings.jobPost.settings.jobTitle',
        workingArrangement: 'jobOpenings.jobPost.settings.workingArrangement',
        next: 'jobOpenings.jobPost.settings.next',
        hybrid: 'jobOpenings.jobPost.settings.hybrid',
        remote: 'jobOpenings.jobPost.settings.remote',
        office: 'jobOpenings.jobPost.settings.office',
      },
      details: {
        roleDetails: 'jobOpenings.jobPost.details.roleDetails',
        importFromJD: 'jobOpenings.jobPost.details.importFromJD',
        undoImports: 'jobOpenings.jobPost.details.undoImports',
        publicFacingPreview: 'jobOpenings.jobPost.details.publicFacingPreview',
        next: 'jobOpenings.jobPost.details.next',
      },
      video: {
        heading: 'jobOpenings.jobPost.video.heading',
        useVideo: 'jobOpenings.jobPost.video.useVideo',
        videoReady: 'jobOpenings.jobPost.video.videoReady',
        selectMicrophone: 'jobOpenings.jobPost.video.selectMicrophone',
        chooseAudioDevice: 'jobOpenings.jobPost.video.chooseAudioDevice',
        titleLabel: 'jobOpenings.jobPost.video.titleLabel',
        titlePlaceholder: 'jobOpenings.jobPost.video.titlePlaceholder',
        description: 'jobOpenings.jobPost.video.description',
        descriptionPlaceholder: 'jobOpenings.jobPost.video.descriptionPlaceholder',
        note: 'jobOpenings.jobPost.video.note',
        startRecording: 'jobOpenings.jobPost.video.startRecording',
        stopRecording: 'jobOpenings.jobPost.video.stopRecording',
        uploadVideo: 'jobOpenings.jobPost.video.uploadVideo',
        supportedFormats: 'jobOpenings.jobPost.video.supportedFormats',
        continue: 'jobOpenings.jobPost.video.continue',
        browserNotSupported: 'jobOpenings.jobPost.video.browserNotSupported',
      },
      preQualifying: {
        heading: 'jobOpenings.jobPost.preQualifying.heading',
        subtitle: 'jobOpenings.jobPost.preQualifying.subtitle',
        previewMode: 'jobOpenings.jobPost.preQualifying.previewMode',
        importFromBank: 'jobOpenings.jobPost.preQualifying.importFromBank',
        searchPlaceholder: 'jobOpenings.jobPost.preQualifying.searchPlaceholder',
        createNew: 'jobOpenings.jobPost.preQualifying.createNew',
        questionText: 'jobOpenings.jobPost.preQualifying.questionText',
        questionPlaceholder: 'jobOpenings.jobPost.preQualifying.questionPlaceholder',
        responseType: 'jobOpenings.jobPost.preQualifying.responseType',
        saveQuestion: 'jobOpenings.jobPost.preQualifying.saveQuestion',
        optionsChoices: 'jobOpenings.jobPost.preQualifying.optionsChoices',
        addChoice: 'jobOpenings.jobPost.preQualifying.addChoice',
        questionList: 'jobOpenings.jobPost.preQualifying.questionList',
        questionsDefined: 'jobOpenings.jobPost.preQualifying.questionsDefined',
        loading: 'jobOpenings.jobPost.preQualifying.loading',
        editQuestion: 'jobOpenings.jobPost.preQualifying.editQuestion',
        type: 'jobOpenings.jobPost.preQualifying.type',
        update: 'jobOpenings.jobPost.preQualifying.update',
        cancel: 'jobOpenings.jobPost.preQualifying.cancel',
        backToVideo: 'jobOpenings.jobPost.preQualifying.backToVideo',
        previewPosting: 'jobOpenings.jobPost.preQualifying.previewPosting',
        duplicateQuestion: 'jobOpenings.jobPost.preQualifying.duplicateQuestion',
        typeNumber: 'jobOpenings.jobPost.preQualifying.typeNumber',
        typeMultipleChoice: 'jobOpenings.jobPost.preQualifying.typeMultipleChoice',
        typeLocation: 'jobOpenings.jobPost.preQualifying.typeLocation',
        typeText: 'jobOpenings.jobPost.preQualifying.typeText',
        choicePlaceholder: 'jobOpenings.jobPost.preQualifying.choicePlaceholder',
      },
      preview: {
        badge: 'jobOpenings.jobPost.preview.badge',
        department: 'jobOpenings.jobPost.preview.department',
        location: 'jobOpenings.jobPost.preview.location',
        workType: 'jobOpenings.jobPost.preview.workType',
        daysLeftLabel: 'jobOpenings.jobPost.preview.daysLeftLabel',
        expired: 'jobOpenings.jobPost.preview.expired',
        expiresToday: 'jobOpenings.jobPost.preview.expiresToday',
        oneDayLeft: 'jobOpenings.jobPost.preview.oneDayLeft',
        daysLeftCount: 'jobOpenings.jobPost.preview.daysLeftCount',
        toApply: 'jobOpenings.jobPost.preview.toApply',
        jobDescription: 'jobOpenings.jobPost.preview.jobDescription',
        readyToApply: 'jobOpenings.jobPost.preview.readyToApply',
        joinTeam: 'jobOpenings.jobPost.preview.joinTeam',
        applyNow: 'jobOpenings.jobPost.preview.applyNow',
        shareOpportunity: 'jobOpenings.jobPost.preview.shareOpportunity',
        helpFindCandidate: 'jobOpenings.jobPost.preview.helpFindCandidate',
        socialNetworks: 'jobOpenings.jobPost.preview.socialNetworks',
        copyUrl: 'jobOpenings.jobPost.preview.copyUrl',
        emailFriend: 'jobOpenings.jobPost.preview.emailFriend',
        urlCopied: 'jobOpenings.jobPost.preview.urlCopied',
        updateJobPost: 'jobOpenings.jobPost.preview.updateJobPost',
        publishJob: 'jobOpenings.jobPost.preview.publishJob',
        updateSubtitle: 'jobOpenings.jobPost.preview.updateSubtitle',
        publishSubtitle: 'jobOpenings.jobPost.preview.publishSubtitle',
        updating: 'jobOpenings.jobPost.preview.updating',
        creating: 'jobOpenings.jobPost.preview.creating',
        updateButton: 'jobOpenings.jobPost.preview.updateButton',
        createButton: 'jobOpenings.jobPost.preview.createButton',
        play: 'jobOpenings.jobPost.preview.play',
        pause: 'jobOpenings.jobPost.preview.pause',
        browserNotSupported: 'jobOpenings.jobPost.preview.browserNotSupported',
      },
      toasts: {
        jobPostUpdated: 'jobOpenings.jobPost.toasts.jobPostUpdated',
        errorUpdating: 'jobOpenings.jobPost.toasts.errorUpdating',
        errorLoadingRequisition: 'jobOpenings.jobPost.toasts.errorLoadingRequisition',
      },
    },
  },
} as const;

export const ASSESSMENT_KEYS = {
  assignedList: {
    welcome: 'assignedList.welcome',
    photoRequired: 'assignedList.photoRequired',
    assessmentName: 'assignedList.assessmentName',
    assessmentDuration: 'assignedList.assessmentDuration',
    questionsRemaining: 'assignedList.questionsRemaining',
    assessmentPoints: 'assignedList.assessmentPoints',
    instructions: 'assignedList.instructions',
    selectLabel: 'assignedList.selectLabel',
    noAssignments: 'assignedList.noAssignments',
    practiceTutorial: 'assignedList.practiceTutorial',
    tooltipPractice: 'assignedList.tooltipPractice',
    confirmCameraStop: 'assignedList.confirmCameraStop',
    turnOffCamera: 'assignedList.turnOffCamera',
    takePhoto: 'assignedList.takePhoto',
    startAssessment: 'assignedList.startAssessment',
    tooltipStart: 'assignedList.tooltipStart',
    minutesUnit: 'assignedList.minutesUnit',
    pointsUnit: 'assignedList.pointsUnit',
  },
  complete: {
    congratulations: 'complete.congratulations',
    candidateDefault: 'complete.candidateDefault',
    successMessage: 'complete.successMessage',
    submittedMessage: 'complete.submittedMessage',
    thankYou: 'complete.thankYou',
    logout: 'complete.logout',
  },
  privacyModal: {
    title: 'privacyModal.title',
    intro: 'privacyModal.intro',
    policyLink: 'privacyModal.policyLink',
    consentIntro: 'privacyModal.consentIntro',
    defaultCompany: 'privacyModal.defaultCompany',
    declineIntro: 'privacyModal.declineIntro',
    declineAll: 'privacyModal.declineAll',
    acceptAll: 'privacyModal.acceptAll',
  },
  selectedAssessment: {
    total: 'selectedAssessment.total',
    points: 'selectedAssessment.points',
    duration: 'selectedAssessment.duration',
    minutes: 'selectedAssessment.minutes',
    disclaimer: 'selectedAssessment.disclaimer',
    warning: 'selectedAssessment.warning',
    practiceTutorial: 'selectedAssessment.practiceTutorial',
    startAssessment: 'selectedAssessment.startAssessment',
  },
  inputSettings: {
    header: 'inputSettings.header',
    cameraPreview: 'inputSettings.cameraPreview',
    cameraDevice: 'inputSettings.cameraDevice',
    cameraDeviceHelp: 'inputSettings.cameraDeviceHelp',
    micDevice: 'inputSettings.micDevice',
    micDeviceHelp: 'inputSettings.micDeviceHelp',
    micLevel: 'inputSettings.micLevel',
    continueAssessment: 'inputSettings.continueAssessment',
  },
  enableCamMic: {
    next: 'enableCamMic.next',
  },
  instructions: {
    step1Title: 'instructions.step1Title',
    step2Title: 'instructions.step2Title',
    step3Title: 'instructions.step3Title',
    step4Title: 'instructions.step4Title',
    step5Title: 'instructions.step5Title',
    step6Title: 'instructions.step6Title',
    completeTitle: 'instructions.completeTitle',
    completeMessage: 'instructions.completeMessage',
    continue: 'instructions.continue',
  },
  browserSettings: {
    heading: 'browserSettings.heading',
    description: 'browserSettings.description',
    javascript: 'browserSettings.javascript',
    javascriptDesc: 'browserSettings.javascriptDesc',
    popupBlockers: 'browserSettings.popupBlockers',
    popupBlockersDesc: 'browserSettings.popupBlockersDesc',
    cookies: 'browserSettings.cookies',
    cookiesDesc: 'browserSettings.cookiesDesc',
    media: 'browserSettings.media',
    mediaDesc: 'browserSettings.mediaDesc',
    required: 'browserSettings.required',
    vpnWarning: 'browserSettings.vpnWarning',
  },
  cameraSetup: {
    privacyNotice: 'cameraSetup.privacyNotice',
    cameraPreview: 'cameraSetup.cameraPreview',
    cameraDevice: 'cameraSetup.cameraDevice',
    cameraDeviceHelp: 'cameraSetup.cameraDeviceHelp',
    defaultCamera: 'cameraSetup.defaultCamera',
    cameraDefault: 'cameraSetup.cameraDefault',
    micDevice: 'cameraSetup.micDevice',
    micDeviceHelp: 'cameraSetup.micDeviceHelp',
    defaultMic: 'cameraSetup.defaultMic',
    micDefault: 'cameraSetup.micDefault',
    micLevel: 'cameraSetup.micLevel',
  },
  systemSetup: {
    defaultTitle: 'systemSetup.defaultTitle',
    pointSingular: 'systemSetup.pointSingular',
    pointPlural: 'systemSetup.pointPlural',
    total: 'systemSetup.total',
    duration: 'systemSetup.duration',
    minutes: 'systemSetup.minutes',
    disclaimer: 'systemSetup.disclaimer',
    warning: 'systemSetup.warning',
    startAssessment: 'systemSetup.startAssessment',
  },
  stepper: {
    prev: 'stepper.prev',
    next: 'stepper.next',
    getStarted: 'stepper.getStarted',
    step: 'stepper.step',
    of: 'stepper.of',
  },
  startRecording: {
    stopRecording: 'startRecording.stopRecording',
    startRecording: 'startRecording.startRecording',
    remainingTime: 'startRecording.remainingTime',
    skillQuestions: 'startRecording.skillQuestions',
    leftToAnswer: 'startRecording.leftToAnswer',
    videoTips: 'startRecording.videoTips',
    tip1: 'startRecording.tip1',
    tip2: 'startRecording.tip2',
    tip3: 'startRecording.tip3',
    questionLabel: 'startRecording.questionLabel',
  },
  stopRecording: {
    responseSubmitted: 'stopRecording.responseSubmitted',
    submittedMessage: 'stopRecording.submittedMessage',
    recordingSaved: 'stopRecording.recordingSaved',
    reviewDisabled: 'stopRecording.reviewDisabled',
    redoRecording: 'stopRecording.redoRecording',
    submitResponse: 'stopRecording.submitResponse',
    remainingTime: 'stopRecording.remainingTime',
    skillQuestions: 'stopRecording.skillQuestions',
    leftToAnswer: 'stopRecording.leftToAnswer',
    questionLabel: 'stopRecording.questionLabel',
  },
  finalSubmit: {
    successTitle: 'finalSubmit.successTitle',
    successSubtitle: 'finalSubmit.successSubtitle',
    stepLabel: 'finalSubmit.stepLabel',
    reviewLabel: 'finalSubmit.reviewLabel',
    description: 'finalSubmit.description',
    statusReview: 'finalSubmit.statusReview',
    statusEncoding: 'finalSubmit.statusEncoding',
    changeMind: 'finalSubmit.changeMind',
    changeMindDesc: 'finalSubmit.changeMindDesc',
    redoRecording: 'finalSubmit.redoRecording',
    readyLabel: 'finalSubmit.readyLabel',
    readyDesc: 'finalSubmit.readyDesc',
    submitNow: 'finalSubmit.submitNow',
  },
  question: {
    seconds: 'question.seconds',
    minuteSingular: 'question.minuteSingular',
    minutesPlural: 'question.minutesPlural',
    and: 'question.and',
    questionLabel: 'question.questionLabel',
    typeSkill: 'question.typeSkill',
    typeVideo: 'question.typeVideo',
    attributeIntro: 'question.attributeIntro',
    attributeSkills: 'question.attributeSkills',
    processingMessage: 'question.processingMessage',
    uploadingStatus: 'question.uploadingStatus',
  },
  questionArea: {
    redoRecording: 'questionArea.redoRecording',
  },
  preScreen: {
    heading: 'preScreen.heading',
    noQuestions: 'preScreen.noQuestions',
    replaceVideo: 'preScreen.replaceVideo',
    uploadVideo: 'preScreen.uploadVideo',
    continueAssessment: 'preScreen.continueAssessment',
    submitError: 'preScreen.submitError',
  },
  behaviouralModal: {
    title: 'behaviouralModal.title',
    description: 'behaviouralModal.description',
    note: 'behaviouralModal.note',
    noteText: 'behaviouralModal.noteText',
    continue: 'behaviouralModal.continue',
    cancel: 'behaviouralModal.cancel',
  },
  warningModal: {
    title: 'warningModal.title',
    aiDetection: 'warningModal.aiDetection',
    aiConsequence: 'warningModal.aiConsequence',
  },
  common: {
    minutes: 'common.minutes',
    points: 'common.points',
    continue: 'common.continue',
    cancel: 'common.cancel',
    next: 'common.next',
  },
} as const;

export const ORG_SETTINGS_KEYS = {
  breadcrumbs: {
    admin: 'breadcrumbs.admin',
    organizationSettings: 'breadcrumbs.organizationSettings',
  },
  tabs: {
    hierarchy: 'tabs.hierarchy',
    general: 'tabs.general',
    subscription: 'tabs.subscription',
  },
  actions: {
    editSettings: 'actions.editSettings',
    exitEditMode: 'actions.exitEditMode',
    editOrganization: 'actions.editOrganization',
    cancel: 'actions.cancel',
    updateOrganization: 'actions.updateOrganization',
  },
  labels: {
    companyName: 'labels.companyName',
    supportEmail: 'labels.supportEmail',
    replyToEmail: 'labels.replyToEmail',
    supportPhone: 'labels.supportPhone',
    perksAndBenefits: 'labels.perksAndBenefits',
    companyDetails: 'labels.companyDetails',
    confirmUpdate: 'labels.confirmUpdate',
    logoPreview: 'labels.logoPreview',
  },
  empty: {
    noPerks: 'empty.noPerks',
    noCompanyDetails: 'empty.noCompanyDetails',
  },
  messages: {
    confirmBeforeUpdate: 'messages.confirmBeforeUpdate',
    updateSuccess: 'messages.updateSuccess',
  },
  logo: {
    changeLogo: 'logo.changeLogo',
    remove: 'logo.remove',
    useDefaultCheckbox: 'logo.useDefaultCheckbox',
    dragDrop: 'logo.dragDrop',
    requirementsTitle: 'logo.requirementsTitle',
    formatRequirement: 'logo.formatRequirement',
    dimensionRequirement: 'logo.dimensionRequirement',
    fileSizeRequirement: 'logo.fileSizeRequirement',
    or: 'logo.or',
    uploadCustom: 'logo.uploadCustom',
    useDefaultButton: 'logo.useDefaultButton',
    companyLogoAlt: 'logo.companyLogoAlt',
    defaultLogoAlt: 'logo.defaultLogoAlt',
    invalidFileType: 'logo.invalidFileType',
    fileTooLarge: 'logo.fileTooLarge',
    uploadSuccess: 'logo.uploadSuccess',
    uploadFailed: 'logo.uploadFailed',
  },
} as const;

export const JOB_PORTAL_KEYS = {
  listing: {
    title: 'listing.title',
    subtitle: 'listing.subtitle',
    availableJobs: 'listing.availableJobs',
    pages: 'listing.pages',
    filters: 'listing.filters',
    filtersAndSearch: 'listing.filtersAndSearch',
    showing: 'listing.showing',
    jobs: 'listing.jobs',
    page: 'listing.page',
    of: 'listing.of',
    view: 'listing.view',
    grid: 'listing.grid',
    list: 'listing.list',
    loading: 'listing.loading',
    findingOpportunities: 'listing.findingOpportunities',
    loadingJobPostings: 'listing.loadingJobPostings',
    noJobsFound: 'listing.noJobsFound',
    noJobsMessage: 'listing.noJobsMessage',
    clearAllFilters: 'listing.clearAllFilters',
  },
  card: {
    location: 'card.location',
    remote: 'card.remote',
    roleOverview: 'card.roleOverview',
    fullTime: 'card.fullTime',
    applyNow: 'card.applyNow',
  },
  filter: {
    searchByJobTitle: 'filter.searchByJobTitle',
    searchByLocation: 'filter.searchByLocation',
    selectWorkType: 'filter.selectWorkType',
    selectDepartment: 'filter.selectDepartment',
    sortBy: 'filter.sortBy',
    search: 'filter.search',
    clearFilter: 'filter.clearFilter',
    hybrid: 'filter.hybrid',
    remote: 'filter.remote',
    office: 'filter.office',
    relevant: 'filter.relevant',
    latestJobs: 'filter.latestJobs',
  },
  details: {
    department: 'details.department',
    location: 'details.location',
    workType: 'details.workType',
    daysLeft: 'details.daysLeft',
    notSpecified: 'details.notSpecified',
    jobDescription: 'details.jobDescription',
    hiring: 'details.hiring',
    privacyNotice: 'details.privacyNotice',
    acceptAll: 'details.acceptAll',
    declineAll: 'details.declineAll',
    importantNotice: 'details.importantNotice',
    phase1: 'details.phase1',
    phase1Text: 'details.phase1Text',
    phase2: 'details.phase2',
    phase2Text: 'details.phase2Text',
    submitting: 'details.submitting',
    finish: 'details.finish',
    next: 'details.next',
    cancel: 'details.cancel',
    back: 'details.back',
    cancelConfirmTitle: 'details.cancelConfirmTitle',
    cancelConfirmYes: 'details.cancelConfirmYes',
    cancelConfirmNo: 'details.cancelConfirmNo',
    cancelConfirmMessage: 'details.cancelConfirmMessage',
    uploadDocuments: 'details.uploadDocuments',
    uploadDocumentsMessage: 'details.uploadDocumentsMessage',
    submitDocuments: 'details.submitDocuments',
    preQualFormTitle: 'details.preQualFormTitle',
  },
  apply: {
    readyToApply: 'apply.readyToApply',
    joinTeam: 'apply.joinTeam',
    applyNow: 'apply.applyNow',
  },
  share: {
    title: 'share.title',
    subtitle: 'share.subtitle',
    copyUrl: 'share.copyUrl',
    emailFriend: 'share.emailFriend',
    socialNetworks: 'share.socialNetworks',
  },
  privacy: {
    beforeLink: 'privacy.beforeLink',
    policyLink: 'privacy.policyLink',
    afterLink: 'privacy.afterLink',
    consentText: 'privacy.consentText',
  },
} as const;

export const AUTH_KEYS = {
  login: {
    leftTitle: 'login.leftTitle',
    leftSubtitle: 'login.leftSubtitle',
    welcome: 'login.welcome',
    signIn: 'login.signIn',
    email: 'login.email',
    password: 'login.password',
    forgotPassword: 'login.forgotPassword',
    loginButton: 'login.loginButton',
    invalidDetails: 'login.invalidDetails',
    resetMailSent: 'login.resetMailSent',
  },
  resetPassword: {
    title: 'resetPassword.title',
    subtitle: 'resetPassword.subtitle',
    emailAddress: 'resetPassword.emailAddress',
    cancel: 'resetPassword.cancel',
    sendResetMail: 'resetPassword.sendResetMail',
  },
  changePassword: {
    leftTitle: 'changePassword.leftTitle',
    leftSubtitle: 'changePassword.leftSubtitle',
    title: 'changePassword.title',
    subtitle: 'changePassword.subtitle',
    password: 'changePassword.password',
    confirmPassword: 'changePassword.confirmPassword',
    cancel: 'changePassword.cancel',
    changeButton: 'changePassword.changeButton',
    success: 'changePassword.success',
  },
  register: {
    leftTitle: 'register.leftTitle',
    leftSubtitle: 'register.leftSubtitle',
    createAccount: 'register.createAccount',
    firstname: 'register.firstname',
    lastname: 'register.lastname',
    email: 'register.email',
    setPassword: 'register.setPassword',
    setPasswordHint: 'register.setPasswordHint',
    password: 'register.password',
    confirmPassword: 'register.confirmPassword',
    createAccountButton: 'register.createAccountButton',
    alreadyHaveAccount: 'register.alreadyHaveAccount',
    signIn: 'register.signIn',
  },
  userLanding: {
    signUp: 'userLanding.signUp',
    setPassword: 'userLanding.setPassword',
    alreadyHaveAccount: 'userLanding.alreadyHaveAccount',
    signIn: 'userLanding.signIn',
    createAccount: 'userLanding.createAccount',
  },
  terms: {
    title: 'terms.title',
    para1: 'terms.para1',
    para2: 'terms.para2',
    para3: 'terms.para3',
    consentContinue: 'terms.consentContinue',
  },
  identityCheck: {
    termsTitle: 'identityCheck.termsTitle',
    title: 'identityCheck.title',
    instruction: 'identityCheck.instruction',
    skip: 'identityCheck.skip',
    takePicture: 'identityCheck.takePicture',
    processingImage: 'identityCheck.processingImage',
    pleaseWait: 'identityCheck.pleaseWait',
    fullFace: 'identityCheck.fullFace',
    lighting: 'identityCheck.lighting',
    completeRegistration: 'identityCheck.completeRegistration',
    errorSettingProfilePic: 'identityCheck.errorSettingProfilePic',
    errorUploadingPicture: 'identityCheck.errorUploadingPicture',
  },
  verifyEmail: {
    title: 'verifyEmail.title',
    sentEmailTo: 'verifyEmail.sentEmailTo',
    toVerify: 'verifyEmail.toVerify',
    didntReceive: 'verifyEmail.didntReceive',
    clickHere: 'verifyEmail.clickHere',
    failedTitle: 'verifyEmail.failedTitle',
    failedMessage: 'verifyEmail.failedMessage',
    successTitle: 'verifyEmail.successTitle',
    toLogin: 'verifyEmail.toLogin',
  },
  unauthorized: {
    title: 'unauthorized.title',
    message: 'unauthorized.message',
    goHome: 'unauthorized.goHome',
    loginAgain: 'unauthorized.loginAgain',
  },
} as const;
